import { useState } from 'react';
import { useBankAccounts, useCreateBankAccount } from '@/hooks/queries/useBankIngestion';
import type { BankAccountDto } from '@signalvote/shared';

interface BankAccountPickerProps {
  selectedAccountId: string | null;
  onSelect: (accountId: string | null) => void;
  /**
   * The file came in via the "Not sure / it's a mix" pick rather than a detected
   * bank statement. Drops the "bank statement" framing from the heading — a mix
   * of transactions may not belong to a single bank account (the no-account
   * choice below is the natural home for it).
   */
  mixedSource?: boolean;
}

const NEW_ACCOUNT_PLACEHOLDER = 'e.g. Operating — Wells Fargo …4567';

/**
 * Sentinel "account id" for the no-account bucket. NOT a real account: when this
 * is the selection, the bank-ingest commit sends the backend `useNoAccountBucket`
 * signal instead of a `bankAccountId`, and the server find-or-creates a
 * campaign-scoped system bucket. Serves the data-entry / compliance user who has
 * a mix of transactions from several accounts and no single bank account to
 * attach it to. Distinct from `null`, which still means "no selection / change
 * account".
 */
export const NO_ACCOUNT_BUCKET = '__no_account_bucket__';

/**
 * Bank-account picker for the import flow. Renders above the file-type
 * detector panel so the treasurer confirms <em>which</em> account they're
 * uploading to before any bytes are detected.
 *
 * "Pick or create" (the import-flow front door): the treasurer can pick an
 * existing account OR create a new one inline — they're never bounced to the
 * Setup tab mid-import. The new account is created with just a display name; the
 * column-mapping profile is learned on the first mapped import, so naming the
 * account once is the only name we ever ask for (the double bank-name collapse).
 *
 * Design doc: <em>"You're uploading to **Fundraising — Wells Fargo …4567**.
 * Continue? [Yes] [Change account]"</em> — the cheapest mistake-prevention
 * affordance the design doc calls out.
 */
export function BankAccountPicker({
  selectedAccountId,
  onSelect,
  mixedSource,
}: BankAccountPickerProps) {
  const { data: accounts, isLoading, error } = useBankAccounts();

  if (isLoading) {
    return (
      <div className="rounded-md border p-4 text-sm text-muted-foreground">
        Loading bank accounts…
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="rounded-md border border-red-200 p-4 text-sm text-red-700">
        Couldn&apos;t load bank accounts. {error instanceof Error ? error.message : ''}
      </div>
    );
  }

  if (selectedAccountId === NO_ACCOUNT_BUCKET) {
    return (
      <div className="rounded-md border bg-card p-4">
        <p className="text-sm">
          No bank account — these are <strong>mixed transactions from multiple
          accounts</strong>. We&apos;ll sort each row in the inbox.
        </p>
        <div className="mt-2 flex gap-3">
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="text-xs text-blue-700 underline hover:text-blue-900"
          >
            Change account
          </button>
        </div>
      </div>
    );
  }

  const selected = accounts?.find((a) => a.id === selectedAccountId);
  if (selected) {
    return (
      <div className="rounded-md border bg-card p-4">
        <p className="text-sm">
          You&apos;re uploading to <strong>{selected.displayName}</strong>.
        </p>
        <div className="mt-2 flex gap-3">
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="text-xs text-blue-700 underline hover:text-blue-900"
          >
            Change account
          </button>
        </div>
      </div>
    );
  }

  const hasAccounts = !!accounts && accounts.length > 0;

  if (!hasAccounts) {
    // Empty state: no Setup-tab dead-end — create the first account right here.
    return (
      <div className="rounded-md border bg-card p-4 space-y-3">
        <div>
          <p className="text-sm font-medium">
            {mixedSource
              ? 'Add an account for these — or choose "No bank account" below if they\'re a mix:'
              : 'Add the account this file is for:'}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            We&apos;ll remember it for next time.
          </p>
        </div>
        <CreateAccountForm onCreated={onSelect} alwaysOpen />
        <NoAccountChoice onSelect={onSelect} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">
        {mixedSource
          ? 'Which account are these transactions for? Or choose "No bank account" if they\'re a mix.'
          : 'Pick the bank account this file is for:'}
      </p>
      <ul className="divide-y rounded-md border bg-card">
        {accounts.map((account: BankAccountDto) => (
          <li key={account.id}>
            <button
              type="button"
              onClick={() => onSelect(account.id)}
              className="flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:bg-muted"
            >
              <span>{account.displayName}</span>
              <span className="text-xs text-muted-foreground">Select</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-col items-start gap-1.5 pt-0.5">
        <CreateAccountForm onCreated={onSelect} />
        <NoAccountChoice onSelect={onSelect} />
      </div>
    </div>
  );
}

/**
 * First-class "I don't have an account for this" choice, alongside the real
 * accounts and the inline-create affordance. Selecting it emits the
 * <code>NO_ACCOUNT_BUCKET</code> sentinel; the commit then sends the backend
 * <code>useNoAccountBucket</code> signal instead of a real account id, so a
 * mixed list of transactions comes in with no account and gets triaged per line.
 */
function NoAccountChoice({
  onSelect,
}: {
  onSelect: (accountId: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(NO_ACCOUNT_BUCKET)}
      className="text-xs text-blue-700 underline hover:text-blue-900"
    >
      No bank account — mixed transactions from multiple accounts
    </button>
  );
}

/**
 * Inline "create a new account" affordance. Collapsed by default (when accounts
 * already exist) behind a "+ New account" button; always open in the empty
 * state. On create, calls the new-account mutation and hands the returned id to
 * <code>onSelect</code> so the freshly-created account is the picked one.
 */
function CreateAccountForm({
  onCreated,
  alwaysOpen,
}: {
  onCreated: (accountId: string) => void;
  alwaysOpen?: boolean;
}) {
  const [open, setOpen] = useState(!!alwaysOpen);
  const [name, setName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const { mutateAsync: createAccount, isPending } = useCreateBankAccount();

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreateError(null);
    try {
      const account = await createAccount({ displayName: trimmed });
      onCreated(account.id);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Could not create the account.');
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-blue-700 underline hover:text-blue-900"
      >
        + New account
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={NEW_ACCOUNT_PLACEHOLDER}
        aria-label="New account name"
        className="w-full max-w-sm rounded-md border px-3 py-1.5 text-sm"
      />
      {createError && (
        <p role="alert" className="text-xs text-red-700">
          {createError}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={create}
          disabled={!name.trim() || isPending}
          className="min-h-11 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {isPending ? 'Adding…' : 'Create'}
        </button>
        {!alwaysOpen && (
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setName('');
              setCreateError(null);
            }}
            disabled={isPending}
            className="min-h-11 rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-60"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
